import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

import { apiPutUsersChangePassword } from "../../api";
import { ApiConnector, RequestError } from "../../api/api-connector";
import { Button } from "../../components/ui-kit/button";
import { Dialog } from "../../components/ui-kit/dialog";
import { useDialog } from "../../components/ui-kit/dialog-provider";
import { PasswordInput } from "../../components/ui-kit/fields/password-input";
import { Form } from "../../components/ui-kit/form/form";
import { FormRow } from "../../components/ui-kit/form/form-row";
import { FormTable } from "../../components/ui-kit/form/form-table";

export function ChangePasswordDialog({
  api,
  onClose,
}: {
  api: ApiConnector;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const { alert } = useDialog();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    // Validate passwords match
    if (newPassword !== repeatPassword) {
      await alert(t`New passwords do not match`);
      return;
    }

    try {
      // First, relogin with old password to get a fresh access token
      await api.relogin(oldPassword);

      // Now change the password
      await apiPutUsersChangePassword({
        body: {
          old_password: oldPassword,
          new_password: newPassword,
          repeat_password: repeatPassword,
        },
        meta: { ignore401: true },
      });

      setSuccess(true);
    } catch (error) {
      if (error instanceof RequestError) {
        if (error.response.status === 401) {
          await alert(t`Incorrect current password`);
        } else if (
          error.data &&
          typeof error.data === "object" &&
          "detail" in error.data
        ) {
          await alert(String(error.data.detail));
        } else {
          await alert(t`Failed to change password`);
        }
      } else {
        throw error;
      }
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <div className="flex flex-col gap-4">
        {success ? (
          <>
            <h2 className="text-base font-semibold">
              <Trans>Password Changed Successfully</Trans>
            </h2>
            <p>
              <Trans>Your password has been changed successfully.</Trans>
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <h2 className="text-base font-semibold mb-4">
              <Trans>Change password</Trans>
            </h2>

            <FormTable dataAdd>
              <FormRow
                label={<Trans>Current password</Trans>}
                data={
                  <PasswordInput
                    autofocus
                    value={oldPassword}
                    onChange={setOldPassword}
                    required
                  />
                }
              />

              <FormRow
                label={<Trans>New password</Trans>}
                data={
                  <PasswordInput
                    value={newPassword}
                    onChange={setNewPassword}
                    required
                  />
                }
              />

              <FormRow
                label={<Trans>Repeat new password</Trans>}
                data={
                  <PasswordInput
                    value={repeatPassword}
                    onChange={setRepeatPassword}
                    required
                  />
                }
              />
            </FormTable>

            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit">
                <Trans>Change password</Trans>
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Dialog>
  );
}
